import { Box, Grid, Typography, Button, CircularProgress, Chip, IconButton, Dialog } from '@material-ui/core';
import React, { memo, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { History as HistoryIcon, VisibilityOutlined, GetApp, CalendarToday, DateRange } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { TooltipMenu } from '../../../../../common/tooltip-menu';

interface AttendanceActivity {
  rosteringId: string;
  activityId: string;
  activityName: string;
  programName: string;
  rosteringStatus: string;
  attendanceStatus: string;
  hours: number | null;
  sessions: Array<{
    sessionId: string;
    sessionName: string;
    attended: boolean;
    hours: number | null;
    startTime: string | null;
    endTime: string | null;
  }>;
  roles: Array<{
    roleId: string;
    roleName: string;
  }>;
}

interface AttendanceDay {
  date: string;
  totalHours: number;
  activities: AttendanceActivity[];
}

interface AttendanceHistoryResponse {
  attendanceHistory: AttendanceDay[];
}

export const VolunteerAttendanceHistorySection = memo(({ volunteerId }: { volunteerId: string }) => {
  const { theme } = useCampfireTheme();
  const {
    volunteerNameOptions: { preferredName },
  } = useVolunteerCommonProfileContext();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const orgInfo = useOrgInfo();

  const [showFuture, setShowFuture] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(0); // 0 = current month, -1 = last month, etc.
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { run } = useCampfireFetch({
    defer: true,
  });

  const loadAttendanceHistory = (monthOffset: number) => {
    setLoading(true);

    const now = DateTime.local();
    let startDate: DateTime;
    let endDate: DateTime;

    if (showFuture) {
      // For future: start from today
      startDate = now.plus({ months: monthOffset }).startOf('month');
      endDate = now.plus({ months: monthOffset }).endOf('month');

      // Don't go beyond today for the start
      if (startDate < now) {
        startDate = now;
      }
    } else {
      // For historical: go backwards from today
      startDate = now.plus({ months: monthOffset }).startOf('month');
      endDate = now.plus({ months: monthOffset }).endOf('month');

      // Don't go beyond today
      if (endDate > now) {
        endDate = now;
      }
    }

    run({
      method: 'post',
      data: {
        volunteerId,
        dateFrom: startDate.toISODate(),
        dateTo: endDate.toISODate(),
      },
      url: `${apiUrl}/vm/volunteer/attendance-history`,
    })
      .then((response) => {
        if (response.ok) {
          const { data } = response.data;
          const attendanceData: AttendanceHistoryResponse = data;
          let history = attendanceData.attendanceHistory || [];

          // For future rosters, reverse to show earliest first
          if (showFuture) {
            history = [...history].reverse();
          }

          setAttendanceHistory(history);
        } else {
          setSnackbar({
            open: true,
            message: 'Error loading attendance history',
            variant: 'error',
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Error loading attendance history',
          variant: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setCurrentMonth(0);
    loadAttendanceHistory(0);
  }, [showFuture, volunteerId]);

  const loadPreviousMonth = () => {
    const newOffset = currentMonth - 1;
    setCurrentMonth(newOffset);
    loadAttendanceHistory(newOffset);
  };

  const loadNextMonth = () => {
    const newOffset = currentMonth + 1;
    setCurrentMonth(newOffset);
    loadAttendanceHistory(newOffset);
  };

  const convertDataToCSV = (data: AttendanceDay[]) => {
    // Create CSV header
    const headers = ['Date', 'Activity', 'Program', 'Session', 'Attended', 'Start Time', 'End Time', 'Hours', 'Status'];

    // Create CSV rows - one row per session
    const rows = data.flatMap((day) =>
      day.activities.flatMap((activity) => {
        // Use activity attendanceStatus to determine if report exists
        const hasReport = activity.attendanceStatus === 'attended' || activity.attendanceStatus === 'absent';

        return activity.sessions.map((session) => {
          // Determine status per session
          let sessionStatus = 'Rostered';
          if (session.attended) {
            sessionStatus = 'Attended';
          } else if (hasReport) {
            // Report exists but this session wasn't attended
            sessionStatus = 'Absent';
          }

          return [
            day.date,
            activity.activityName,
            activity.programName,
            session.sessionName,
            session.attended ? 'Yes' : 'No',
            session.startTime || '',
            session.endTime || '',
            session.hours !== null ? session.hours.toFixed(2) : '',
            sessionStatus,
          ]
            .map((cell) => `"${cell}"`)
            .join(',');
        });
      })
    );

    // Combine header and rows
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const fetchAttendanceData = async (startDate: DateTime, endDate: DateTime): Promise<AttendanceDay[]> => {
    const response = await run({
      method: 'post',
      data: {
        volunteerId,
        dateFrom: startDate.toISODate(),
        dateTo: endDate.toISODate(),
      },
      url: `${apiUrl}/vm/volunteer/attendance-history`,
    });

    if (response.ok) {
      const { data } = response.data;
      const attendanceData: AttendanceHistoryResponse = data;
      return attendanceData.attendanceHistory || [];
    }
    return [];
  };

  const exportThisMonth = async () => {
    setIsExporting(true);
    setExportMenuOpen(false);

    try {
      const now = DateTime.local();
      const startDate = now.startOf('month');
      const endDate = now.endOf('month');

      const data = await fetchAttendanceData(startDate, endDate);
      const csv = convertDataToCSV(data);
      downloadCSV(csv, `${preferredName}_attendance_this_month_${now.toFormat('yyyy-MM-dd')}.csv`);

      setSnackbar({
        open: true,
        message: 'Export completed successfully',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error exporting data',
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportThisYear = async () => {
    setIsExporting(true);
    setExportMenuOpen(false);

    try {
      const now = DateTime.local();
      const allData: AttendanceDay[] = [];

      // Fetch data month by month for this year (sequential requests)
      const fetchMonthSequentially = async (monthIndex: number): Promise<void> => {
        if (monthIndex >= now.month) return;

        const startDate = now.set({ month: monthIndex + 1 }).startOf('month');
        const endDate = now.set({ month: monthIndex + 1 }).endOf('month');

        const data = await fetchAttendanceData(startDate, endDate);
        allData.push(...data);

        await fetchMonthSequentially(monthIndex + 1);
      };

      await fetchMonthSequentially(0);

      const csv = convertDataToCSV(allData);
      downloadCSV(csv, `${preferredName}_attendance_this_year_${now.toFormat('yyyy')}.csv`);

      setSnackbar({
        open: true,
        message: 'Export completed successfully',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error exporting data',
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportAllTime = async () => {
    setIsExporting(true);
    setExportMenuOpen(false);

    try {
      if (!orgInfo?.dateCreated) {
        setSnackbar({
          open: true,
          message: 'Unable to determine organization start date',
          variant: 'error',
        });
        setIsExporting(false);
        return;
      }

      const now = DateTime.local();
      const startDate = orgInfo.dateCreated;
      const allData: AttendanceDay[] = [];

      // Fetch data month by month from org creation to now (sequential requests)
      const fetchAllMonthsSequentially = async (currentDate: DateTime): Promise<void> => {
        if (currentDate > now) return;

        const monthEnd = currentDate.endOf('month');
        const data = await fetchAttendanceData(currentDate, monthEnd);
        allData.push(...data);

        await fetchAllMonthsSequentially(currentDate.plus({ months: 1 }));
      };

      await fetchAllMonthsSequentially(startDate.startOf('month'));

      const csv = convertDataToCSV(allData);
      downloadCSV(csv, `${preferredName}_attendance_all_time_${now.toFormat('yyyy-MM-dd')}.csv`);

      setSnackbar({
        open: true,
        message: 'Export completed successfully',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error exporting data',
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusChipColor = (rosteringStatus: string, attendanceStatus: string) => {
    // Priority: attendance status over rostering status
    if (attendanceStatus === 'attended') {
      return theme.color.rosters.status.completed.secondary; // Dark green
    }
    if (attendanceStatus === 'absent') {
      return theme.status.amber.dark; // Dark orange
    }
    if (rosteringStatus === 'declined') {
      return theme.color.rosters.status.cancelled.secondary; // Red/brown
    }
    // Default: rostered (availability blue)
    return theme.color.rosters.status.available.secondary; // Availability blue
  };

  const getStatusLabel = (rosteringStatus: string, attendanceStatus: string) => {
    // Priority: attendance status first
    if (attendanceStatus === 'attended') return 'Attended';
    if (attendanceStatus === 'absent') return 'Absent';

    // Fall back to rostering status if no attendance reported
    if (rosteringStatus === 'declined') return 'Declined';
    return 'Rostered';
  };

  const canGoBack = showFuture ? currentMonth > 0 : currentMonth > -12; // Future: can go back if moved forward; Historical: limit to 12 months back
  const canGoForward = showFuture ? true : currentMonth < 0; // Future: can always go forward; Historical: can go forward toward present

  return (
    <>
      {/* Export Loading Dialog */}
      <Dialog open={isExporting}>
        <Box padding='40px' display='flex' flexDirection='column' alignItems='center'>
          <CircularProgress />
          <Typography variant='body1' style={{ marginTop: '20px', fontWeight: 500 }}>
            Exporting attendance data...
          </Typography>
        </Box>
      </Dialog>

      <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
        <Grid item xs={12} style={{ textAlign: 'left' }}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <Box>
              <Typography
                style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
              >
                <HistoryIcon style={{ fontSize: '14px', paddingRight: '5px' }} /> Attendance History
              </Typography>
              <Typography variant='body2' style={{ color: '#464646' }} align='left'>
                {preferredName}&apos;s rostering and attendance records
              </Typography>
            </Box>
            <Box display='flex'>
              <TooltipMenu
                open={exportMenuOpen}
                onClose={() => setExportMenuOpen(false)}
                title={
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                  >
                    <Button
                      startIcon={<CalendarToday style={{ fontSize: '16px' }} />}
                      onClick={exportThisMonth}
                      style={{
                        color: theme.alert.blue.extraLight,
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                      }}
                    >
                      This Month
                    </Button>
                    <Button
                      startIcon={<DateRange style={{ fontSize: '16px' }} />}
                      onClick={exportThisYear}
                      style={{
                        color: theme.alert.blue.extraLight,
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                      }}
                    >
                      This Year
                    </Button>
                    <Button
                      startIcon={<HistoryIcon style={{ fontSize: '16px' }} />}
                      onClick={exportAllTime}
                      style={{
                        color: theme.alert.blue.extraLight,
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                      }}
                    >
                      All Time
                    </Button>
                  </Box>
                }
              >
                <IconButton
                  style={{
                    height: 36,
                    width: 36,
                    borderRadius: 6,
                    border: '1px solid #DCDCDC',
                    color: theme?.color?.grey?.neutral400,
                    padding: 0,
                    marginTop: '4px',
                    marginRight: '8px',
                  }}
                  onClick={() => setExportMenuOpen(true)}
                  disabled={isExporting}
                >
                  <GetApp style={{ fontSize: '18px' }} />
                </IconButton>
              </TooltipMenu>
              <Button
                variant='outlined'
                startIcon={<VisibilityOutlined />}
                style={{
                  height: 36,
                  borderRadius: 6,
                  border: '1px solid #DCDCDC',
                  color: theme?.color?.grey?.neutral400,
                  textTransform: 'capitalize',
                  marginTop: '4px',
                }}
                onClick={() => setShowFuture(!showFuture)}
              >
                <Typography
                  style={{
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '19px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {`Viewing: ${showFuture ? 'Future' : 'Historical'}`}
                </Typography>
              </Button>
            </Box>
          </Box>
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />

          {/* Month Navigation */}
          <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom='16px'>
            <Button
              variant='outlined'
              size='small'
              onClick={loadPreviousMonth}
              disabled={!canGoBack || loading}
              style={{ textTransform: 'capitalize' }}
            >
              Previous Month
            </Button>
            <Typography variant='body2' style={{ fontWeight: 600 }}>
              {DateTime.local()
                .plus({ months: currentMonth })
                .toFormat('MMMM yyyy')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              onClick={loadNextMonth}
              disabled={!canGoForward || loading}
              style={{ textTransform: 'capitalize' }}
            >
              Next Month
            </Button>
          </Box>

          {loading ? (
            <Box display='flex' justifyContent='center' padding='40px'>
              <CircularProgress />
            </Box>
          ) : attendanceHistory.length === 0 ? (
            <Typography
              variant='body2'
              style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' }}
            >
              No attendance records found for this period
            </Typography>
          ) : (
            <Box>
              {attendanceHistory.map((day) => {
                const dateObj = DateTime.fromISO(day.date);

                return (
                  <Box key={day.date} marginBottom='16px'>
                    {/* Card */}
                    <Box
                      border={`1px solid ${theme.color.grey.border}`}
                      borderRadius='8px'
                      overflow='hidden'
                      bgcolor={theme.color.white.neutral50}
                    >
                      {/* Header: Date on LEFT, Hours on RIGHT - SAME ROW */}
                      <Box display='flex' alignItems='stretch' borderBottom={`1px solid ${theme.color.grey.border}`}>
                        {/* Date badge on left */}
                        <Box
                          display='flex'
                          flexDirection='column'
                          alignItems='center'
                          justifyContent='center'
                          padding='10px 16px'
                          bgcolor={theme.color.grey.neutralBrand200}
                          minWidth='70px'
                        >
                          <Typography
                            style={{
                              fontSize: '18px',
                              fontWeight: 900,
                              color: theme.color.grey.neutral400,
                              lineHeight: 1,
                            }}
                          >
                            {dateObj.day}
                          </Typography>
                          <Typography
                            style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: theme.color.grey.neutral300,
                              marginTop: '2px',
                            }}
                          >
                            {dateObj.toFormat('MMM yy')}
                          </Typography>
                        </Box>

                        {/* Hours info on right */}
                        <Box
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                          flex={1}
                          padding='8px 12px'
                          bgcolor={theme.color.grey.neutralBrand100}
                        >
                          <Typography style={{ fontSize: '12px', fontWeight: 600, color: theme.color.grey.neutral300 }}>
                            {showFuture ? 'Scheduled Hours' : 'Attended Hours'}
                          </Typography>
                          <Typography style={{ fontSize: '14px', fontWeight: 700, color: theme.color.primary.main900 }}>
                            {day.totalHours.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Activities */}
                      <Box padding='10px'>
                        {day.activities.map((activity) => {
                          const statusLabel = getStatusLabel(activity.rosteringStatus, activity.attendanceStatus);
                          const chipBackgroundColor = getStatusChipColor(
                            activity.rosteringStatus,
                            activity.attendanceStatus
                          );

                          // White text for all chips
                          const chipTextColor = theme.color.white.neutral50;

                          // Calculate scheduled times from sessions
                          let scheduledTime = '';
                          if (activity.sessions.length > 0) {
                            const times = activity.sessions
                              .filter((s) => s.startTime && s.endTime)
                              .map((s) => `${s.startTime} - ${s.endTime}`);
                            scheduledTime = times[0] || '';
                          }

                          return (
                            <Box
                              key={activity.rosteringId}
                              padding='8px 10px'
                              marginBottom='6px'
                              borderRadius='6px'
                              bgcolor='#F9F9F9'
                            >
                              <Box display='flex' alignItems='flex-start' justifyContent='space-between'>
                                <Box flex={1}>
                                  <Box display='flex' alignItems='center' marginBottom='2px'>
                                    <Typography style={{ fontSize: '13px', fontWeight: 700, marginRight: '12px' }}>
                                      {activity.activityName}
                                    </Typography>
                                    <Chip
                                      label={statusLabel}
                                      size='small'
                                      style={{
                                        backgroundColor: chipBackgroundColor,
                                        color: chipTextColor,
                                        fontSize: '9px',
                                        height: '20px',
                                        fontWeight: 600,
                                        padding: '0 10px',
                                        borderRadius: '6px',
                                      }}
                                    />
                                  </Box>
                                  <Typography style={{ fontSize: '11px', color: theme.color.grey.neutral200 }}>
                                    {activity.programName}
                                  </Typography>
                                  {activity.roles.length > 0 && (
                                    <Typography
                                      style={{ fontSize: '10px', color: theme.color.grey.neutral300, marginTop: '2px' }}
                                    >
                                      {activity.roles.map((r) => r.roleName).join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                                <Box textAlign='right' flexShrink={0} marginLeft='12px'>
                                  {activity.hours !== null && (
                                    <Typography style={{ fontSize: '12px', fontWeight: 700, marginBottom: '2px' }}>
                                      {activity.hours.toFixed(2)}
                                    </Typography>
                                  )}
                                  {scheduledTime && (
                                    <Typography style={{ fontSize: '10px', color: theme.color.grey.neutral300 }}>
                                      {scheduledTime}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Sessions */}
                              {activity.sessions.length > 0 && (
                                <Box
                                  marginTop='6px'
                                  paddingTop='6px'
                                  borderTop={`1px solid ${theme.color.grey.border}`}
                                >
                                  {activity.sessions.map((session) => {
                                    // Determine if report exists for this activity
                                    const hasReport =
                                      activity.attendanceStatus === 'attended' ||
                                      activity.attendanceStatus === 'absent';

                                    // Show times if attended OR rostered (no report), show cross if absent
                                    let displayText = '✗';
                                    if (session.attended && session.startTime && session.endTime) {
                                      // Attended: show actual times
                                      displayText = `${session.startTime} - ${session.endTime}`;
                                    } else if (!hasReport && session.startTime && session.endTime) {
                                      // Rostered (no report): show scheduled times
                                      displayText = `${session.startTime} - ${session.endTime}`;
                                    }
                                    // Else: Absent (has report but not attended) - show cross

                                    return (
                                      <Box
                                        key={session.sessionId}
                                        display='flex'
                                        justifyContent='space-between'
                                        marginTop='3px'
                                      >
                                        <Typography style={{ fontSize: '11px', color: theme.color.grey.neutral300 }}>
                                          {session.sessionName}
                                        </Typography>
                                        <Typography
                                          style={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            color: theme.color.grey.neutral400,
                                          }}
                                        >
                                          {displayText}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
});
